/* The person's permissions and the checks on them, with no React in the file,
   so tests can run the same check the shell and the pages use. */

export type Verb = "view" | "create" | "edit" | "delete";
export const VERBS: Verb[] = ["view", "create", "edit", "delete"];
export type Matrix = Record<string, Verb[]>;
/** resource to record id to verbs: grants limited to one dashboard, one creator, one niche. */
export type ScopedMatrix = Record<string, Record<string, Verb[]>>;
export type ResourceRow = { key: string; module: string; moduleName: string; label: string; description?: string; verbs: Verb[]; scopeLabel?: string };

export type Me = {
  user: { id: string; username: string; /** Their name when set, else the username. */ name?: string; isOwner: boolean };
  matrix: Matrix;
  scoped: ScopedMatrix;
  resources: ResourceRow[];
  modules: { id: string; name: string; description: string }[];
  ai: { configured: boolean };
};

/** "research.pick:view" to its parts. A bare resource means view. */
export function parseNeeds(needs: string): [string, Verb] {
  const [resource, verb] = needs.split(":");
  return [resource!, (verb as Verb) ?? "view"];
}

/**
 * Whether they hold it anywhere: globally, or on at least one record. The
 * right question for "show this page" and "show this nav item"; the page
 * itself only receives the records they may see.
 */
export function canIn(me: Me | undefined, needs: string): boolean {
  if (!me) return false;
  if (me.user.isOwner) return true;
  const [resource, verb] = parseNeeds(needs);
  if (me.matrix[resource]?.includes(verb)) return true;
  return Object.values(me.scoped?.[resource] ?? {}).some((verbs) => verbs.includes(verb));
}

/** One permission, or several of which any one is enough. */
export type Needs = string | readonly string[];

/**
 * Whether they hold any of them, in the sense of canIn. For a page several
 * kinds of person reach for different parts of it (API/AI Usage: the admin
 * figures for one, HikerAPI for another), each part checking its own.
 */
export function canAnyIn(me: Me | undefined, needs: Needs): boolean {
  return typeof needs === "string" ? canIn(me, needs) : needs.some((one) => canIn(me, one));
}

/** Whether they hold it for one record. For actions on a row. */
export function canAtIn(me: Me | undefined, needs: string, scope: string): boolean {
  if (!me) return false;
  if (me.user.isOwner) return true;
  const [resource, verb] = parseNeeds(needs);
  return (me.matrix[resource]?.includes(verb) ?? false) || (me.scoped?.[resource]?.[scope]?.includes(verb) ?? false);
}
